import {all, call, put, takeLatest} from '@redux-saga/core/effects';
import {push} from 'connected-react-router';
import {Async, AsyncError, AsyncResult, Loaded, Loading} from 'Core/models/Async';
import {createValidatedEmail, ValidEmail} from 'Core/models/Email';
import {NonEmptyString} from 'Core/models/EmptyString';
import {createValidatedNonEmptyString} from 'Core/models/ValidatableNonEmptyString';
import {
  createValidatableEmptyPhone,
  createValidatedPhone,
  OptionalPhoneNumber,
  ValidPhoneNumber,
} from 'Core/models/PhoneNumber';
import {flatten} from 'fp-ts/lib/Array';
import {Either, fold as foldEither} from 'fp-ts/lib/Either';
import {identity} from 'fp-ts/lib/function';
import {pipe} from 'fp-ts/lib/pipeable';
import {map as mapTaskEither} from 'fp-ts/lib/TaskEither';
import {ValidRegionCode} from 'Agent/Policies/models/RegionCode';
import {getApiCallGenerator, getValidationSaga} from 'Shared/utilities/sagaApiGenerators';
import userApi from 'Users/api/userApi';
import {
  changeAvailableFSLs,
  changeFieldVicePresidentUser,
  changeRegions,
  FieldVicePresidentUserActionType,
  getAvailableFSLS,
  getFvpUser,
  getRegions,
  loadFvpUser,
  saveFieldVicePresidentUser,
  setEmail,
  setPhone,
  setUsername,
} from 'Users/ducks/fieldVicePresidentUser';
import {UserTypeModel, UserTypeModelKey} from 'Users/models/Claims';
import {ErrorFieldVicePresident, SavedFieldVicePresident} from 'Users/models/FieldVicePresident';
import {UserPayload} from 'Users/models/UserPayload';
import {UserRouteConstants} from 'Users/Routes';

export function* validateEmailSaga() {
  yield takeLatest(
    FieldVicePresidentUserActionType.VALIDATE_EMAIL,
    getValidationSaga(
      ValidEmail.create,
      setEmail,
      userApi.validateEmail,
      value => ValidEmail.create(value) instanceof ValidEmail
    )
  );
}

export function* validatePhoneSaga() {
  yield takeLatest(
    FieldVicePresidentUserActionType.VALIDATE_PHONE,
    getValidationSaga(
      ValidPhoneNumber.create,
      setPhone,
      userApi.validatePhoneNumber,
      value => ValidPhoneNumber.create(value) instanceof ValidPhoneNumber
    )
  );
}

export function* validateUsernameSaga() {
  yield takeLatest(
    FieldVicePresidentUserActionType.VALIDATE_USERNAME,
    getValidationSaga(NonEmptyString.create, setUsername, userApi.validateAllstateUsername)
  );
}
const getFvpUserFromApi = (action: ReturnType<typeof getFvpUser>) => {
  const getFieldSalesLeaders = (user: UserPayload) =>
    flatten(
      user.userTypes.map(
        UserTypeModel.match({
          [UserTypeModelKey.AgentSite]: agentClaims => agentClaims.fieldSalesLeaders,
          default: () => [],
        })
      )
    );

  return pipe(
    () => userApi.getUser(action.payload.userId),
    mapTaskEither(
      user =>
        new SavedFieldVicePresident(
          NonEmptyString.create(user.firstName) as NonEmptyString,
          NonEmptyString.create(user.lastName) as NonEmptyString,
          createValidatedNonEmptyString(user.userName),
          user.mobilePhone ? createValidatedPhone(user.mobilePhone) : createValidatableEmptyPhone(),
          createValidatedEmail(user.email),
          getFieldSalesLeaders(user),
          user.id
        )
    )
  )();
};
export function* getFvpSaga() {
  yield takeLatest(
    FieldVicePresidentUserActionType.FETCH_FVP_USER,
    getApiCallGenerator(getFvpUserFromApi, loadFvpUser)
  );
}
export function* getRegionsApi(action: ReturnType<typeof getRegions>) {
  const regionCodesEither: AsyncResult<ValidRegionCode[]> = yield call(userApi.getRegionCodes);
  const asyncRegions = pipe(
    regionCodesEither,
    foldEither(identity, regions => new Loaded(regions) as Async<ValidRegionCode[]>)
  );
  yield put(changeRegions(asyncRegions));
}
export function* getRegionsSaga() {
  yield takeLatest(FieldVicePresidentUserActionType.GET_REGIONS, getRegionsApi);
}
export function* saveFieldVicePresidentUserGenerator(action: ReturnType<typeof saveFieldVicePresidentUser>) {
  yield put(changeFieldVicePresidentUser(new Loading()));

  const fvpResult: Either<any, any> = yield call(
    userApi.saveFieldVicePresidentUser,
    action.payload.fvp,
    action.payload.id
  );

  if (action.payload.id) {
    yield call(userApi.saveFVPFieldSalesLeaders, action.payload.fvp, action.payload.id);
  }

  function* onSave() {
    yield put(push(UserRouteConstants.USERS));
  }
  function* onError(errorDetails: AsyncError) {
    yield put(changeFieldVicePresidentUser(new Loading()));

    const errorResult = new ErrorFieldVicePresident(
      action.payload.fvp.firstName,
      action.payload.fvp.lastName,
      action.payload.fvp.username,
      action.payload.fvp.phoneNumber as OptionalPhoneNumber,
      action.payload.fvp.email,
      action.payload.fvp.fieldSalesLeaders,
      errorDetails
    );

    yield put(changeFieldVicePresidentUser(new Loaded(errorResult)));
  }

  yield pipe(fvpResult, foldEither(onError, onSave as any));
}
export function* saveFVPUserSaga() {
  yield takeLatest(FieldVicePresidentUserActionType.SAVE_FVP_USER, saveFieldVicePresidentUserGenerator);
}

export function getAvailableSalesLeadersApi(action: ReturnType<typeof getAvailableFSLS>) {
  return userApi.getAvailableSalesLeaders() as any;
}

export function* getAvailableFieldSalesLeadersSaga() {
  yield takeLatest(
    FieldVicePresidentUserActionType.GET_AVAILABLE_FSLS,
    getApiCallGenerator(getAvailableSalesLeadersApi, changeAvailableFSLs)
  );
}

export default function*() {
  yield all([
    saveFVPUserSaga(),
    getFvpSaga(),
    validateUsernameSaga(),
    validateEmailSaga(),
    validatePhoneSaga(),
    getAvailableFieldSalesLeadersSaga(),
  ]);
}
